import type { MatrixData } from '../components/MatrixTable'

import { getOfflineDb } from '../offline/dbFactory'
import { ensureOfflineReady } from '../offline/initOffline'
import { enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { drainEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { api } from './api'
import {
  buildQueuedEntityMutationResult,
  type EntityMutationResult,
  type EntityMutationSyncState
} from './entityMutationSync'
import {
  cacheMatrix,
  cloneMatrix,
  getDefaultMatrixForSpecies,
  hasRecentLocalMortalityMatrixEdit,
  loadCachedMatrix,
  markLocalMortalityMatrixEdit
} from './mortalityMatrix'

type LoadMortalityMatrixOptions = {
  forceRefresh?: boolean
  preferRecentLocal?: boolean
}

export type LoadMortalityMatrixResult = {
  matrix: MatrixData
  syncState: EntityMutationSyncState
  errorMessage: string | null
  // 'server'  — the returned matrix is the authoritative server copy.
  // 'local'   — the returned matrix is the local cache / species default,
  //             either because a local edit is still pending sync or because
  //             the server fetch failed (see `errorMessage`).
  source: 'server' | 'local'
}

const isOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine

const getLocalMatrix = (species: string): MatrixData => (
  cloneMatrix(loadCachedMatrix(species) ?? getDefaultMatrixForSpecies(species))
)

export const readMortalityMatrixSyncState = async (species: string): Promise<EntityMutationSyncState> => {
  await ensureOfflineReady()

  const pendingCount = await getOfflineDb().entityOutbox
    .where('[entityType+entityId]')
    .equals(['mortalityMatrix', species])
    .count()

  if (pendingCount === 0) {
    return 'synced'
  }

  return isOnline() ? 'pending' : 'offline'
}

export const loadMortalityMatrix = async (
  species: string,
  options: LoadMortalityMatrixOptions = {}
): Promise<LoadMortalityMatrixResult> => {
  const localMatrix = getLocalMatrix(species)
  const syncState = await readMortalityMatrixSyncState(species)

  if (options.preferRecentLocal && hasRecentLocalMortalityMatrixEdit(species)) {
    return {
      matrix: localMatrix,
      syncState,
      errorMessage: null,
      source: 'local'
    }
  }

  if (syncState !== 'synced') {
    return {
      matrix: localMatrix,
      syncState,
      errorMessage: null,
      source: 'local'
    }
  }

  try {
    const record = await api.mortalityMatrix.get(species, { noCache: options.forceRefresh })
    const matrix = cloneMatrix(record.matrix as MatrixData)
    cacheMatrix(species, matrix)
    return {
      matrix,
      syncState: 'synced',
      errorMessage: null,
      source: 'server'
    }
  } catch (error) {
    return {
      matrix: localMatrix,
      syncState: 'synced',
      errorMessage: error instanceof Error ? error.message : 'Failed to load mortality matrix',
      source: 'local'
    }
  }
}

export const saveMortalityMatrix = async (
  species: string,
  matrix: MatrixData
): Promise<EntityMutationResult<MatrixData>> => {
  const nextMatrix = cloneMatrix(matrix)
  cacheMatrix(species, nextMatrix)
  markLocalMortalityMatrixEdit(species)

  await ensureOfflineReady()
  await enqueueEntityChange({
    entityType: 'mortalityMatrix',
    op: 'upsert',
    entityId: species,
    payload: {
      species,
      matrix: nextMatrix
    }
  })

  return buildQueuedEntityMutationResult(nextMatrix, {
    entityType: 'mortalityMatrix',
    entityId: species,
    maxEntries: 25,
    maxPasses: 5
  })
}

export const retryMortalityMatrixSync = async (species: string): Promise<EntityMutationSyncState> => {
  if (!isOnline()) {
    return 'offline'
  }

  try {
    await drainEntityOutboxNow({
      maxEntries: 25,
      maxPasses: 5
    })
  } catch (error) {
    console.warn('[mortalityMatrixApi] failed to drain pending mortality matrix changes', error)
  }

  return readMortalityMatrixSyncState(species)
}